"use client";

import Image from "next/image";
import YouTube from "react-youtube";
import AnimatedContent from "../AnimatedContent";
import { useTranslation } from "react-i18next";
import SectionContainer from "@/common/SectionContainer";

interface GlobalMarketCard {
  src: string;
  title: string;
  desc: string;
}

const GlobalMarketSection = () => {
  const { t } = useTranslation();
  const videoId = "KdYS5RrGtvw";

  const youtubeOpts = {
    height: "100%",
    width: "100%",
    playerVars: {
      autoplay: 0,
      modestbranding: 1,
      rel: 0,
    },
  };

  const onPlayerError = (event: unknown) => {
    console.warn("YouTube Player Error:", event);
  };

  const cards = t("GLOBAL_MARKET_SECTION_CARDS", {
    returnObjects: true,
  }) as GlobalMarketCard[];

  return (
    <SectionContainer
      id="global-market-section"
      className="section-shell bg-white text-gray-900"
    >
      <AnimatedContent className="section-inner section-stack">
        <div className="section-head">
          <h2 className="section-title">{t("GLOBAL_MARKET_SECTION_TITLE")}</h2>
          <p className="caption text-gray-600 section-lead">
            {t("GLOBAL_MARKET_SECTION_SUB_TITLE_1")}
            <br className="hidden sm:block" />
            {t("GLOBAL_MARKET_SECTION_SUB_TITLE_2")}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
          <div className="relative w-full lg:w-1/2 aspect-video rounded-2xl overflow-hidden shadow-lg">
            <YouTube
              videoId={videoId}
              opts={youtubeOpts}
              onError={onPlayerError}
              className="w-full h-full"
            />
          </div>

          <div className="relative w-full lg:w-1/2 aspect-[257/347] rounded-2xl overflow-hidden shadow-lg flex items-center justify-center">
            <Image
              src="https://zipshow-static-web.s3.ap-northeast-2.amazonaws.com/homepage/color-to-life-biz/global_market_1.png"
              alt={t("GLOBAL_MARKET_SECTION_MAIN_IMAGE_ALT")}
              fill
              quality={90}
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 50vw"
            />
          </div>
        </div>

        <div className="space-y-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cards.map((card, i) => (
              <div
                key={i}
                className="flex flex-col bg-white rounded-2xl shadow-md hover:shadow-lg transition-all overflow-hidden"
              >
                <div className="relative w-full aspect-[4/3]">
                  <Image
                    src={card.src}
                    alt={card.title}
                    fill
                    quality={95}
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, 33vw"
                  />
                </div>
                <div className="p-6 text-center flex flex-col gap-2">
                  <h4 className="font-semibold text-[clamp(1.1rem,1.6vw,1.25rem)] text-gray-900">
                    {card.title}
                  </h4>
                  <p className="text-gray-600 text-[clamp(0.9rem,1.2vw,1rem)] leading-relaxed">
                    {card.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-700 text-[clamp(0.95rem,1.3vw,1.05rem)] leading-relaxed mx-auto">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5BBF4D] to-[#207AE8] font-bold">
              {t("GLOBAL_MARKET_SECTION_BOTTOM_POINT_1")}
            </span>
            {t("GLOBAL_MARKET_SECTION_BOTTOM_TEXT_1")}
            <br className="hidden sm:block" />
            {t("GLOBAL_MARKET_SECTION_BOTTOM_TEXT_2")}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5BBF4D] to-[#207AE8] font-bold">
              {t("GLOBAL_MARKET_SECTION_BOTTOM_POINT_2")}
            </span>
            {t("GLOBAL_MARKET_SECTION_BOTTOM_TEXT_3")}
          </p>
        </div>
      </AnimatedContent>
    </SectionContainer>
  );
};

export default GlobalMarketSection;
